import { Component, HostBinding, Input, ChangeDetectionStrategy, ViewEncapsulation,
         AfterContentInit, OnDestroy, Output, EventEmitter, OnInit, QueryList, forwardRef, ContentChild } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { BaseDirective } from '../../base.directive';
import { UiStateService } from './services/ui-state.service';

import { EuiCardHeaderComponent } from './components/eui-card-header/eui-card-header.component';
import { EuiCardMediaComponent } from './components/eui-card-media/eui-card-media.component';
import { EuiCardContentComponent } from './components/eui-card-content/eui-card-content.component';
import { EuiCardFooterComponent } from './components/eui-card-footer/eui-card-footer.component';

@Component({
    templateUrl: './eui-card.component.html',
    selector: 'eui-card',
    styleUrls: ['./styles/_index.scss'],
    providers: [UiStateService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class EuiCardComponent extends BaseDirective implements AfterContentInit, OnDestroy, OnInit {

    @Input()
    get euiSelected() { return this._euiSelected; }
    set euiSelected(value) { this._euiSelected = coerceBooleanProperty(value); }
    _euiSelected = false;

    @Input()
    get euiCollapsible() { return this._euiCollapsible; }
    set euiCollapsible(value) { this._euiCollapsible = coerceBooleanProperty(value); }
    _euiCollapsible = false;

    @Input()
    get euiCollapsed() { return this._euiCollapsed; }
    set euiCollapsed(value) { this._euiCollapsed = coerceBooleanProperty(value); }
    _euiCollapsed = false;

    @Input()
    get euiUrgent() { return this._euiUrgent; }
    set euiUrgent(value) { this._euiUrgent = coerceBooleanProperty(value); }
    _euiUrgent = false;

    @Input()
    get euiNoShadow() { return this._euiNoShadow; }
    set euiNoShadow(value) { this._euiNoShadow = coerceBooleanProperty(value); }
    _euiNoShadow = false;

    @Input()
    get euiNoContentPadding() { return this._euiNoContentPadding; }
    set euiNoContentPadding(value) { this._euiNoContentPadding = coerceBooleanProperty(value); }
    _euiNoContentPadding = false;

    @Input()
    get euiHoverable() { return this._euiHoverable; }
    set euiHoverable(value) { this._euiHoverable = coerceBooleanProperty(value); }
    _euiHoverable = false;

    // WILL BE DEPRECATED IN 13+
    @Input()
    get euiFlat() { return this._euiFlat; }
    set euiFlat(value) {
        console.warn('***WARNING ! euiFlat option will become the default and will be deprecated in eUI13+');
        this._euiFlat = coerceBooleanProperty(value);
    }
    _euiFlat = true;

    // DEPRECATED - Will be dropped in eUI13+
    @Input()
    get euiInverted() { return this._euiInverted; }
    set euiInverted(value) {
        console.warn('***WARNING ! euiInverted option is deprecated and will be dropped in eUI13+. Use euiSelect instead');
        this._euiInverted = coerceBooleanProperty(value);
    }
    _euiInverted = false;

    // TO BE DEPRECATED in favour of base directive euiHighlighted
    @Input()
    get euiEditable() { return this._euiEditable; }
    set euiEditable(value) {
        console.warn('***WARNING ! euiEditable is deprecated and should be replaced by euiHighlighted');
        this._euiEditable = coerceBooleanProperty(value);
    }
    _euiEditable = false;

    @HostBinding('class')
    get cssClasses() {
        return [
            super.getCssClasses('eui-card'),
            this._euiCollapsible ? `eui-card--collapsible` : ``,
            this._euiCollapsed ? `eui-card--collapsed` : ``,
            this._euiSelected ? `eui-card--selected` : ``,
            this._euiUrgent ? `eui-card--urgent` : ``,
            this._euiNoShadow ? `eui-card--no-shadow` : ``,
            this._euiNoContentPadding ? `eui-card--no-content-padding` : ``,
            this._euiHoverable ? `eui-card--hoverable` : ``,
            this._euiFlat ? `eui-card--flat` : ``,              // TO BE DROPPED IN 13+
            this._euiInverted ? `eui-card--inverted` : ``,      // TO BE DROPPED IN 13+ in favour of euiSelected
            this._euiEditable ? `eui-card--highlighted` : ``,   // TO BE DROPPED IN 13+ in favour of euiHighlighted
        ].join(' ').trim();
    }
    @HostBinding('attr.data-e2e') @Input() e2eAttr: string = 'eui-card';

    @Output() onCollapsed: EventEmitter<any> = new EventEmitter();

    @ContentChild(forwardRef(() => EuiCardHeaderComponent), { static: false }) cardHeader: QueryList<EuiCardHeaderComponent>;
    @ContentChild(forwardRef(() => EuiCardMediaComponent), { static: false }) cardMedia: QueryList<EuiCardMediaComponent>;
    @ContentChild(forwardRef(() => EuiCardContentComponent), { static: false }) cardContent: QueryList<EuiCardContentComponent>;
    @ContentChild(forwardRef(() => EuiCardFooterComponent), { static: false }) cardFooter: QueryList<EuiCardFooterComponent>;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    /**
     * TODO: a11y features to be added.
     * Since there is no a11y card pattern (https://a11yproject.com/patterns/)
     * Consider using Material CdK and the FocusMonitor injectable service :
     * See: https://material.angular.io/cdk/a11y/overview
     */
    constructor( public uiStateService: UiStateService ) {
        super();
    }

    ngOnInit() {
        const nextState = {
            ...this.uiStateService.state,
            isCollapsible: this.euiCollapsible,
            isCollapsed: this.euiCollapsed,
        };
        this.uiStateService.setState(nextState);
    }

    ngAfterContentInit() {
        this.uiStateService.state$.pipe(takeUntil(this.destroy$)).subscribe( (state: any) => {
            this._euiCollapsible = state.isCollapsible;
            this._euiCollapsed = state.isCollapsed;
            this.onCollapsed.emit(this._euiCollapsed);
        });
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
